/**
 * B-24b WS-1 extension: contract fields (value, term start/end, account
 * owner) are optional at create-time. They are sent via a separate
 * ``PUT /admin/tenants/{id}/contract`` call after provisioning, so the
 * provision endpoint shape is unchanged. An empty form section is shown
 * with a note that it can be filled later from the tenant detail page.
 *
 * 2026-06-16 SA-console redesign: flat form restructured into a header
 * band + 2-col CollapsibleCard layout matching DemoDetail.tsx HoD spec.
 * Layout only — all handlers, request shapes, and validation preserved.
 */
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, FileText, UserPlus } from "lucide-react";
import {
  useProvisionTenant,
  useUpsertTenantContract,
} from "@/hooks/queries/admin/useAdminTenants";
import { CollapsibleCard } from "@/components/common/CollapsibleCard";
import { Button } from "@/components/common/Button";
import { TERMS_VERSION, DPA_VERSION, PRIVACY_VERSION } from "@/lib/legalVersions";

export default function TenantCreate(): JSX.Element {
  const navigate = useNavigate();
  const provision = useProvisionTenant();

  const [companyName, setCompanyName] = useState("");
  const [slug, setSlug] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  // Batch 27h drive-by fix: split from a single ``adminName`` to match the
  // backend's first/last contract since Batch 27f.
  const [adminFirstName, setAdminFirstName] = useState("");
  const [adminLastName, setAdminLastName] = useState("");

  // Manual per-contract pilot length. Defaults to the standard 45-day free
  // pilot; an admin can override it per contract. Blank sends null (backend
  // applies the 45-day default). Validated as a whole number >= 1 when present.
  const [trialLengthDays, setTrialLengthDays] = useState("45");
  const [trialError, setTrialError] = useState<string | null>(null);

  // B-24b WS-1 — optional contract fields.
  const [contractValueDollars, setContractValueDollars] = useState("");
  const [contractTermStart, setContractTermStart] = useState("");
  const [contractTermEnd, setContractTermEnd] = useState("");
  const [accountOwner, setAccountOwner] = useState("");
  // Screening cap (DC2 fair_use_ceiling). Optional; blank = NULL = unlimited
  // (fail-open). Validated >= 1 when present, matching the DB CHECK + the
  // UpsertContractBody `ge=1` constraint. Threaded through the same
  // post-provision contract upsert as the other contract fields.
  const [screeningCap, setScreeningCap] = useState("");
  const [capError, setCapError] = useState<string | null>(null);

  // We need a tenant id to call the contract endpoint; set after provision.
  const [newTenantId, setNewTenantId] = useState<string | null>(null);
  const upsertContract = useUpsertTenantContract(newTenantId ?? "");

  const hasContractData =
    contractValueDollars.trim() !== "" ||
    contractTermStart.trim() !== "" ||
    contractTermEnd.trim() !== "" ||
    accountOwner.trim() !== "" ||
    screeningCap.trim() !== "";

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setCapError(null);
    setTrialError(null);
    // Validate the screening cap up-front (blank = unlimited; >= 1 otherwise).
    let capValue: number | null = null;
    if (screeningCap.trim() !== "") {
      const parsed = Number(screeningCap);
      if (!Number.isInteger(parsed) || parsed < 1) {
        setCapError("Screening cap must be a whole number ≥ 1, or blank for unlimited.");
        return;
      }
      capValue = parsed;
    }
    // Pilot length (blank = backend default of 45; >= 1 whole days otherwise).
    let trialLengthValue: number | null = null;
    if (trialLengthDays.trim() !== "") {
      const parsedLen = Number(trialLengthDays);
      if (!Number.isInteger(parsedLen) || parsedLen < 1) {
        setTrialError("Pilot length must be a whole number of days ≥ 1, or blank for the 45-day default.");
        return;
      }
      trialLengthValue = parsedLen;
    }
    provision.mutate(
      {
        company_name: companyName,
        slug: slug || null,
        admin_email: adminEmail,
        admin_first_name: adminFirstName,
        admin_last_name: adminLastName,
        plan_id: null,
        company_website: null,
        terms_version: TERMS_VERSION,
        dpa_version: DPA_VERSION,
        privacy_version: PRIVACY_VERSION,
        trial_length_days: trialLengthValue,
      },
      {
        onSuccess: (r) => {
          const tid = r.tenant.id;
          if (hasContractData) {
            // Submit contract fields then navigate.
            setNewTenantId(tid);
            const dollarNum =
              contractValueDollars.trim() === ""
                ? null
                : Number(contractValueDollars);
            upsertContract.mutate(
              {
                contract_value_cents:
                  dollarNum != null && !Number.isNaN(dollarNum)
                    ? Math.round(dollarNum * 100)
                    : null,
                contract_term_start: contractTermStart.trim() || null,
                contract_term_end: contractTermEnd.trim() || null,
                account_owner: accountOwner.trim() || null,
                fair_use_ceiling: capValue,
              },
              {
                onSettled: () => {
                  navigate(`/tenants/${tid}`);
                },
              },
            );
          } else {
            navigate(`/tenants/${tid}`);
          }
        },
      },
    );
  }

  const inputClass =
    "w-full rounded border border-brand-200 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-surface-canvas dark:text-text-primary dark:focus:ring-brand-teal";
  const labelClass =
    "mb-1 block text-sm font-medium text-text-secondary dark:text-brand-100/70";

  const isPending = provision.isPending || upsertContract.isPending;

  return (
    <div className="mx-auto w-full max-w-5xl space-y-4">
      {/* ── HEADER BAND ─────────────────────────────────────────────────────── */}
      <section className="rounded-xl border border-border-default bg-surface-card px-5 py-4">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-surface-subtle text-text-tertiary">
            <Building2 size={18} aria-hidden />
          </span>
          <div className="min-w-0">
            <p className="text-2xs font-bold uppercase tracking-[0.14em] text-text-tertiary">
              New Tenant
            </p>
            <h1 className="truncate text-xl font-bold tracking-tight text-text-primary">
              Provision Tenant
            </h1>
          </div>
        </div>
      </section>

      <form onSubmit={onSubmit}>
        {/* ── 2-COL CARD ROW ────────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {/* Company Info */}
          <CollapsibleCard
            title="Company Info"
            defaultOpen
            leadingIcon={<Building2 size={14} aria-hidden />}
          >
            <div className="space-y-3 pt-1">
              <div>
                <label className={labelClass}>Company name</label>
                <input
                  required
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>
                  Slug (optional — auto-generated from name)
                </label>
                <input
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  placeholder="acme"
                  pattern="^[a-z0-9][a-z0-9-]*[a-z0-9]$"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Pilot length (days)</label>
                <input
                  type="number"
                  min={1}
                  step={1}
                  inputMode="numeric"
                  placeholder="45"
                  value={trialLengthDays}
                  onChange={(e) => setTrialLengthDays(e.target.value)}
                  className={inputClass}
                />
                <p className="mt-1 text-xs text-text-secondary dark:text-brand-100/50">
                  Standard free pilot is 45 days. Override per contract, or leave
                  blank for the default.
                </p>
                {trialError ? (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                    {trialError}
                  </p>
                ) : null}
              </div>
            </div>
          </CollapsibleCard>

          {/* Contract */}
          <CollapsibleCard
            title="Contract"
            defaultOpen
            leadingIcon={<FileText size={14} aria-hidden />}
          >
            <div className="space-y-3 pt-1">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Contract value ($ / year)</label>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    placeholder="e.g. 24000"
                    value={contractValueDollars}
                    onChange={(e) => setContractValueDollars(e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Account owner</label>
                  <input
                    type="text"
                    maxLength={256}
                    placeholder="e.g. Jane Smith"
                    value={accountOwner}
                    onChange={(e) => setAccountOwner(e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Term start</label>
                  <input
                    type="date"
                    value={contractTermStart}
                    onChange={(e) => setContractTermStart(e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Renewal date (term end)</label>
                  <input
                    type="date"
                    value={contractTermEnd}
                    onChange={(e) => setContractTermEnd(e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>
              <div>
                <label className={labelClass}>Screening cap / high-water mark</label>
                <input
                  type="number"
                  min={1}
                  step={1}
                  inputMode="numeric"
                  placeholder="e.g. 500"
                  value={screeningCap}
                  onChange={(e) => setScreeningCap(e.target.value)}
                  className={inputClass}
                />
                <p className="mt-1 text-xs text-text-secondary dark:text-brand-100/50">
                  Per-tenant monthly screen cap. Leave blank for unlimited.
                </p>
                {capError ? (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                    {capError}
                  </p>
                ) : null}
              </div>
            </div>
          </CollapsibleCard>
        </div>

        {/* ── ADMIN USER (full width) ──────────────────────────────────────── */}
        <CollapsibleCard
          title="Admin User Details"
          defaultOpen
          leadingIcon={<UserPlus size={14} aria-hidden />}
        >
          <div className="space-y-3 pt-1">
            <div>
              <label className={labelClass}>Admin email</label>
              <input
                required
                type="email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                className={inputClass}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Admin first name</label>
                <input
                  required
                  value={adminFirstName}
                  onChange={(e) => setAdminFirstName(e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Admin last name</label>
                <input
                  required
                  value={adminLastName}
                  onChange={(e) => setAdminLastName(e.target.value)}
                  className={inputClass}
                />
              </div>
            </div>
          </div>
        </CollapsibleCard>

        {provision.isError ? (
          <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
            {provision.error.message}
          </div>
        ) : null}

        <div className="mt-3 flex items-center gap-2">
          <Button type="submit" variant="primary" disabled={isPending}>
            Provision
          </Button>
          <Button type="button" variant="secondary" onClick={() => navigate("/tenants")}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
